import React from "react";
import Footer from "../../../components/Footer/Footer";
import {
  Brand,
  ContactBlock,
  Content,
  Header,
  HeroHeadline,
  List,
  PageWrapper,
  Paragraph,
  Section,
  SectionHeading,
  Strong,
  UpdatedDate,
} from "../Policy/styled";

const DesktopPrivacyPolicy: React.FC = () => {
  const effectiveDate = "February 10, 2025";

  return (
    <PageWrapper>
      <Header>
        <Brand>Cabinet Logger</Brand>
        <HeroHeadline>Privacy Policy</HeroHeadline>
        <UpdatedDate>Effective {effectiveDate}</UpdatedDate>
        <Paragraph>
          Blunt Solutions, LLC (“Cabinet Logger,” “we,” “our,” or “us”) is
          headquartered in Dallas, Texas. This Privacy Policy explains how we
          collect, use, disclose, and safeguard information when you visit our
          desktop experiences, manage shipments, or interact with our support
          team. By using our services, you agree to the practices described
          here.
        </Paragraph>
      </Header>

      <Content>
        <Section>
          <SectionHeading>Information We Collect</SectionHeading>
          <Paragraph>
            We collect information that helps us activate your account, deliver
            core logistics features, and support compliance requirements. This
            includes:
          </Paragraph>
          <List>
            <li>
              <Strong>Contact details</Strong> such as name, email address,
              phone number, and dealer business address when you register,
              onboard new users, or request support.
            </li>
            <li>
              <Strong>Account and role identifiers</Strong> supplied by
              Firebase Authentication and our internal admin tooling so we can
              assign the correct manufacturer, dealer, or driver permissions.
            </li>
            <li>
              <Strong>Operational data</Strong> about the shipments you manage,
              including barcode scan events, status changes, and any notes you
              provide when interacting with the platform.
            </li>
            <li>
              <Strong>Precise location</Strong> coordinates captured when you
              opt to attach drop-off verification to a scan. Location capture is
              optional, but if you grant permission we store the coordinates
              with the related item history.
            </li>
            <li>
              <Strong>Purchase and subscription metadata</Strong> generated when
              you launch a Stripe checkout session to activate paid features.
              Stripe processes payment credentials directly—we do not receive
              card or bank numbers.
            </li>
            <li>
              <Strong>Device and interaction context</Strong> such as the items
              you scan, buttons you click, and the timestamp of updates so we
              can reconcile offline edits once you reconnect.
            </li>
          </List>
        </Section>

        <Section>
          <SectionHeading>How We Use Information</SectionHeading>
          <List>
            <li>Authenticate users and secure access to dealer data.</li>
            <li>
              Track cabinet shipments, maintain audit trails, and deliver the
              logistics functionality you request.
            </li>
            <li>
              Sync offline actions once you regain connectivity and resolve data
              conflicts.
            </li>
            <li>
              Provide customer support, including role assignments and account
              provisioning.
            </li>
            <li>
              Meet contractual or legal obligations, including safety and loss
              prevention documentation.
            </li>
            <li>
              Improve product reliability by understanding how the platform is
              used (for example, which workflows require additional guidance).
            </li>
          </List>
        </Section>

        <Section>
          <SectionHeading>Legal Bases for Processing</SectionHeading>
          <Paragraph>
            We process personal information to perform our contracts with
            manufacturers, dealers, and drivers; to comply with legal
            obligations (such as record retention); and to operate Cabinet
            Logger’s legitimate interests in securing the platform, preventing
            fraud, and improving product performance. Where required, we rely on
            your consent—for example, when you grant location permissions within
            the app.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>How We Share Information</SectionHeading>
          <Paragraph>
            We do not sell personal information. We share information only with:
          </Paragraph>
          <List>
            <li>
              <Strong>Service providers</Strong> that help us operate Cabinet
              Logger, including Firebase (authentication, databases, storage),
              Google Cloud (hosting, maps, and geocoding), Stripe (billing), and
              Capacitor plugins (device capabilities).
            </li>
            <li>
              <Strong>Your organization</Strong> (manufacturers, dealers, or
              drivers) based on role-based access so the correct teammates see
              shipment status, drop-off confirmations, and audit trails.
            </li>
            <li>
              <Strong>Authorities or professional advisors</Strong> when
              required by law or to protect the rights, safety, or property of
              Cabinet Logger, our users, or the public.
            </li>
          </List>
          <Paragraph>
            Each partner is bound by contractual obligations to protect your
            information and to use it only for the services they provide to us.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>Data Retention and Security</SectionHeading>
          <Paragraph>
            Shipment history and scan records are retained for the life of the
            dealer or manufacturer relationship so you can evidence delivery
            milestones. User accounts are retained while active and deleted or
            anonymized when you initiate the <Strong>Delete Account</Strong>
            action in the app or submit a verified request to our privacy team.
            When a deletion request is confirmed, we remove profile details and
            authentication records within 30 days, while preserving shipment or
            billing logs that we are legally required to keep. We use encryption
            in transit, role-based access controls, and structured audit logs to
            safeguard data. No system is perfectly secure, so please notify us
            immediately if you become aware of unauthorized access.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>Your Choices and Rights</SectionHeading>
          <Paragraph>
            Depending on your location, you may have the right to request
            access, correction, deletion, or a copy of your personal
            information. You can also object to certain processing or request
            that we limit how we use your data. To exercise any of these rights,
            contact us using the details below. We will respond consistent with
            applicable U.S. state and international laws.
          </Paragraph>
          <List>
            <li>
              <Strong>Delete Account:</Strong> Signed-in admins can open Settings
              &gt; Delete Account to start the self-service workflow. We verify
              the request via email and remove credentials once outstanding
              shipments or billing holds are resolved. You can also email
              privacy@bluntsolutions.com if you prefer a manual review.
            </li>
            <li>
              <Strong>Access &amp; Portability:</Strong> Request a copy of the
              data tied to your dealer profile, including shipment histories and
              user audit logs.
            </li>
            <li>
              <Strong>Corrections:</Strong> Ask us to fix inaccurate contact or
              role information so your organization’s records stay current.
            </li>
            <li>
              <Strong>Objections:</Strong> Limit certain analytics or optional
              notifications that are not required to operate the platform.
            </li>
          </List>
        </Section>

        <Section>
          <SectionHeading>International Transfers</SectionHeading>
          <Paragraph>
            Cabinet Logger is operated from the United States. If you access the
            platform from outside the U.S., your information may be transferred
            to, stored in, and processed in the United States. We take steps to
            ensure any transfers comply with applicable data protection laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>Children’s Privacy</SectionHeading>
          <Paragraph>
            Cabinet Logger is designed for professional use and is not intended
            for children under 16. We do not knowingly collect personal
            information from children. If you believe a child has provided us
            information, please contact us so we can delete it.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>Changes to This Policy</SectionHeading>
          <Paragraph>
            We may update this Privacy Policy to reflect operational, legal, or
            regulatory changes. We will post the revised statement with an
            updated effective date and, when appropriate, provide additional
            notice.
          </Paragraph>
        </Section>

        <Section>
          <SectionHeading>Contact Us</SectionHeading>
          <Paragraph>
            If you have questions about this Privacy Policy or need to exercise
            your privacy rights, please reach out.
          </Paragraph>
          <ContactBlock>
            <span>
              <Strong>Blunt Solutions, LLC</Strong>
            </span>
            <span>Cabinet Logger Privacy Office</span>
            <span>Suite 1200, 1919 McKinney Ave.</span>
            <span>Dallas, TX 75201</span>
            <span>Email: privacy@bluntsolutions.com</span>
            <span>Phone: (214) 555-0199</span>
          </ContactBlock>
        </Section>
      </Content>

      <Footer />
    </PageWrapper>
  );
};

export default DesktopPrivacyPolicy;
